import type { FeatureFlagUser, IFeatureFlagsProvider } from './FeatureFlagsProvider'

/** Canonical flag keys and defaults (aligned with backend FEATURE_FLAG_DICT). */
export const DEFAULT_STATIC_FEATURE_FLAGS: Readonly<Record<string, boolean>> = {
  'add-customer-to-asset-required-maintenance': true,
  add_solution_lines_in_case_with_part_desc: true,
  'agg-data-new': true,
  'allow-mandatory-labels': true,
  asset_id_name_fix: true,
  asset_rm_teams: true,
  attribution_model: true,
  azure_map_location: false,
  case_population: false,
  collect_customers_latest_dataset: true,
  concat_mv_func_fix: true,
  copy_csv_between_clients: true,
  customer_duplicate_lines: true,
  customer_risk_score_refactor: true,
  'dependent-mapping-fields': true,
  division_patches: true,
  fcr_fix_failed_metric: true,
  filter_shotgun_active_agents: true,
  'fix-product-types': true,
  fix_dominant_heuristic_missing_values: true,
  fix_fcr_calculation_like_ftf: false,
  fix_ftf_failed_metric_specific_date_issue: true,
  fix_initialize_column_types: true,
  fix_numeric_validation: true,
  fix_part_reliability_install_date_eq_event_date: true,
  fix_shotgun_analysis_fail_on_part_id: true,
  fix_shotgun_analysis_ratio: true,
  fix_wpi_recommendation_error: true,
  fix_wpi_recommendation_previous_month: true,
  ftf_predction_only_field_fix: true,
  include_soft_solutions: false,
  insight_anywhere: true,
  insight_anywhere_top_performance: true,
  insights_anywhere_opi: true,
  long_results_handling: false,
  'metadata-injection': false,
  new_ars: true,
  new_executive_nlg: true,
  'nlp-upload-download': true,
  nlp_aas: true,
  observation_cost_refactor: true,
  observation_investigation_crash: true,
  part_name_as_string: true,
  part_reliability_add_doa_elf_count_columns: true,
  part_reliability_handle_temp: true,
  'partial-files-upload': true,
  preventable: true,
  'product-hierarchy-asset-required-maintenance': true,
  'product-hierarchy-parts-multivalue': true,
  'product-hierarchy-workforce': true,
  'product-quality': false,
  'product-type-hierarchy-parts': false,
  'product-type-name-hierarchy': true,
  'producttype-hierarchy-multivalue': true,
  'producttype-rename': true,
  reference_custom_text_column: true,
  reference_remove_dups: true,
  refresh_materialized_view: false,
  remove_mtbf_part_reliability: true,
  sales_force_bootcamp: true,
  save_datasets_to_db: true,
  sc_events_merge_issue: true,
  scop_generate_links: false,
  'shotgun-analysis-filter-minimum-events-per-tech-part': true,
  solution_name_fix: false,
  team_performance_index: true,
  team_recommendation: true,
  team_t1_hierarchy: true,
  triage_targets: false,
  wpi_all_products: true,
  wpi_handler_improve_months_of_experience: true,
  wpi_recommendation_all_products: true,
}

/**
 * Feature flags served from an in-memory map (build-time defaults).
 * For a remote SDK, add another class that implements `IFeatureFlagsProvider` the same way.
 */
export class StaticFeatureFlagsProvider implements IFeatureFlagsProvider {
  constructor(
    private readonly flags: Readonly<Record<string, boolean>> = DEFAULT_STATIC_FEATURE_FLAGS
  ) {}

  async init(): Promise<void> {
    /* sync — nothing to load */
  }

  variation(key: string, defaultValue: boolean): boolean {
    const v = this.flags[key]
    return v !== undefined ? v : defaultValue
  }

  allFlags(): Record<string, boolean> {
    return { ...this.flags }
  }

  identify(_user: FeatureFlagUser): void {
    /* no-op for static flags */
  }
}
